import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';

import { useAuth, useRecruiterId } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useShare, type ShareOutcome } from '../lib/use-share.js';
import { logger } from '../lib/logger.js';
import { platformBrandsRepo } from '../lib/repositories/earnings-repo.js';
import { recruitersRepo } from '../lib/repositories/recruiters-repo.js';
import type { PlatformBrand, RecruiterProfile } from '@kilat/shared';

import { PageShell } from './recruiter-home.js';

/**
 * /recruiter/code — recruiter's "personal version" of the public
 * `/r/{recruiterCode}` page (Sprint 85). Sibling of the agent /code
 * page; the key difference is that the QR encodes the agent-signup
 * URL with `?ref={recruiterCode}`, NOT the operator-signup URL:
 *
 *   https://{brand.domains.marketing}/become-an-agent?ref={code}
 *
 * That's because a recruiter's job is to bring AGENTS on board (not
 * operators directly) — those agents then bring operators on, and
 * recruiter commissions cascade from the operators' paid invoices.
 *
 * Print-friendly: `@media print` strips chrome so the laminated card
 * fills the page when the recruiter hits Cmd/Ctrl+P.
 */
export function RecruiterCodePage() {
  const { user, loading: authLoading } = useAuth();
  const recruiterId = useRecruiterId();
  const t = useI18n();
  const platformBrand = usePlatformBrand();
  const [profile, setProfile] = useState<RecruiterProfile | null>(null);
  // `profileReady` distinguishes "first snapshot pending" from "arrived as null"
  // (missing/unparseable doc), so the latter renders an explicit gone state
  // rather than a permanent skeleton ([[feedback-no-dead-ends]]).
  const [profileReady, setProfileReady] = useState(false);
  const [brand, setBrand] = useState<PlatformBrand | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const { outcome, share, copy } = useShare();

  const retry = () => {
    setError(null);
    setProfile(null);
    setProfileReady(false);
    setBrand(null);
    setRetryNonce((n) => n + 1);
  };

  useEffect(() => {
    if (!recruiterId) {
      setProfile(null);
      setProfileReady(false);
      return undefined;
    }
    const unsub = recruitersRepo.snapshot(
      recruiterId,
      (v) => {
        setProfile(v);
        setProfileReady(true);
      },
      (e) => setError(e.message),
    );
    return unsub;
  }, [recruiterId, retryNonce]);

  useEffect(() => {
    if (!profile?.platformBrandId) {
      setBrand(null);
      return undefined;
    }
    const unsub = platformBrandsRepo.snapshot(
      profile.platformBrandId,
      (v) => {
        if (!v) {
          setError(`Brand "${profile.platformBrandId}" not found.`);
          return;
        }
        setError(null);
        setBrand(v);
      },
      (e) => setError(e.message),
    );
    return unsub;
  }, [profile?.platformBrandId, retryNonce]);

  const agentSignupUrl = useMemo(() => {
    if (!brand || !profile) return null;
    return `https://${brand.domains.marketing}/become-an-agent?ref=${encodeURIComponent(profile.code)}`;
  }, [brand, profile]);

  if (authLoading)
    return (
      <PageShell>
        <CardSkeleton />
      </PageShell>
    );
  if (!user) {
    return (
      <PageShell>
        <EmptyCard
          title={t.t('agent.payouts.signedOutTitle')}
          body={t.t('agent.recruiterCode.signedOutBody')}
        />
      </PageShell>
    );
  }
  if (!recruiterId) {
    return (
      <PageShell>
        <EmptyCard
          title={t.t('agent.payouts.notRecruiterTitle')}
          body={t.t('agent.recruiterCode.notRecruiterBody')}
          ctaLabel={t.t('agent.payouts.notRecruiterCta')}
          ctaHref={`https://${platformBrand.domains.marketing}/become-a-recruiter`}
        />
      </PageShell>
    );
  }
  if (error) {
    return (
      <PageShell>
        <EmptyCard
          title={t.t('agent.cardGone.recruiterTitle')}
          body={t.t('agent.cardGone.body')}
          onRetry={retry}
          retryLabel={t.t('agent.cardGone.retry')}
        />
      </PageShell>
    );
  }
  if (profileReady && !profile) {
    return (
      <PageShell>
        <EmptyCard
          title={t.t('agent.profileGone.recruiterTitle')}
          body={t.t('agent.profileGone.body')}
          onRetry={retry}
          retryLabel={t.t('agent.profileGone.retry')}
        />
      </PageShell>
    );
  }
  if (!profile || !brand || !agentSignupUrl) {
    return (
      <PageShell>
        <CardSkeleton />
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Phone: the actions, then the card. Tablet and up: side by side. */}
      <div className="ag-pair">
        <header className="no-print" style={{ marginBottom: 'var(--space-2)' }}>
          <h1 style={{ margin: 0, fontSize: 'var(--text-h1)', fontWeight: 600 }}>
            {t.t('agent.recruiterCode.cardTitle')}
          </h1>
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('agent.recruiterCode.cardIntro')}
          </p>
          <div
            style={{
              marginTop: 'var(--space-4)',
              display: 'flex',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => {
                void share({
                  title: t.t('agent.recruiterCode.shareTitle', {
                    brand: brand.name,
                    name: profile.name,
                  }),
                  text: t.t('agent.recruiterCode.shareText', { brand: brand.name }),
                  url: agentSignupUrl,
                });
              }}
              style={primaryButton}
            >
              {t.t('agent.code.shareLink')}
            </button>
            <button
              type="button"
              onClick={() => {
                void copy(agentSignupUrl);
              }}
              style={secondaryButton}
            >
              {t.t('agent.code.copyLink')}
            </button>
            <button type="button" onClick={() => window.print()} style={secondaryButton}>
              {t.t('agent.code.print')}
            </button>
            <ShareOutcomeNote outcome={outcome} />
          </div>
        </header>

        <ShareCard
          url={agentSignupUrl}
          brandName={brand.name}
          recruiterName={profile.name}
          code={profile.code}
        />
      </div>
    </PageShell>
  );
}

function ShareCard({
  url,
  brandName,
  recruiterName,
  code,
  size = 320,
}: {
  readonly url: string;
  readonly brandName: string;
  readonly recruiterName: string;
  readonly code: string;
  readonly size?: number;
}) {
  const t = useI18n();
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null);
  const [drawError, setDrawError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSvgMarkup(null);
    setDrawError(false);
    QRCode.toString(url, {
      type: 'svg',
      errorCorrectionLevel: 'H',
      margin: 1,
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then((svg) => {
        if (cancelled) return;
        setSvgMarkup(svg);
      })
      .catch((e) => {
        logger.error('recruiter qr generation failed', { url, error: String(e) });
        if (cancelled) return;
        setDrawError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <article
      className="recruiter-share-card"
      style={{
        width: '100%',
        maxWidth: 480,
        padding: 'var(--space-6)',
        borderRadius: 'var(--radius-lg)',
        background: '#ffffff',
        color: '#0f172a',
        border: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-2)',
        textAlign: 'center',
        display: 'grid',
        gap: 'var(--space-4)',
        margin: '0 auto',
      }}
    >
      <header>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            letterSpacing: 4,
            textTransform: 'uppercase',
            color: 'rgba(15,23,42,0.55)',
            margin: 0,
          }}
        >
          {t.t('agent.recruiterCode.recruiterEyebrow', { brand: brandName })}
        </p>
        <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 600, margin: 'var(--space-2) 0 0 0' }}>
          {recruiterName}
        </h2>
        <p
          style={{
            fontSize: 'var(--text-body)',
            color: 'rgba(15,23,42,0.7)',
            margin: 'var(--space-2) 0 0 0',
          }}
        >
          {t.t('agent.recruiterCode.scanJoin', { brand: brandName })}
        </p>
      </header>

      <div
        aria-label={t.t('agent.code.qrAria')}
        style={{
          width: size,
          height: size,
          margin: '0 auto',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        dangerouslySetInnerHTML={svgMarkup ? { __html: svgMarkup } : undefined}
      >
        {svgMarkup ? null : (
          <span style={{ fontSize: 'var(--text-meta)', color: 'rgba(15,23,42,0.45)' }}>
            {drawError ? t.t('agent.code.drawError') : t.t('agent.code.drawingQr')}
          </span>
        )}
      </div>

      <footer style={{ display: 'grid', gap: 'var(--space-1)' }}>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'rgba(15,23,42,0.55)',
            margin: 0,
          }}
        >
          {t.t('agent.recruiterCode.codeLabel')}
        </p>
        <p
          style={{
            fontSize: 'var(--text-h2)',
            fontWeight: 600,
            letterSpacing: 2,
            fontFamily: 'ui-monospace, monospace',
            margin: 0,
          }}
        >
          {code}
        </p>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'rgba(15,23,42,0.55)',
            margin: 'var(--space-2) 0 0 0',
          }}
        >
          {t.t('agent.code.orVisit')}
        </p>
        <p style={{ fontSize: 'var(--text-body)', wordBreak: 'break-all', margin: 0 }}>
          {humanUrl(url)}
        </p>
      </footer>

      <style>{`
        @media print {
          @page { margin: 16mm; }
          body { background: #ffffff !important; }
          .recruiter-share-card {
            box-shadow: none !important;
            border: none !important;
            page-break-inside: avoid;
          }
        }
      `}</style>
    </article>
  );
}

function humanUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname === '/' ? '' : u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

function ShareOutcomeNote({ outcome }: { readonly outcome: ShareOutcome }) {
  if (outcome === 'idle' || outcome === 'cancelled') return null;
  const text =
    outcome === 'shared'
      ? 'Shared.'
      : outcome === 'copied'
        ? 'Copied to clipboard.'
        : 'Could not share or copy. Long-press the link below to copy manually.';
  return (
    <span
      role="status"
      style={{
        alignSelf: 'center',
        fontSize: 'var(--text-meta)',
        color: outcome === 'failed' ? '#b45309' : 'var(--color-primary)',
      }}
    >
      {text}
    </span>
  );
}

function CardSkeleton() {
  const t = useI18n();
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 480,
        height: 540,
        margin: '0 auto',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-border)',
      }}
      aria-busy="true"
      aria-label={t.t('agent.recruiterCode.loadingCardAria')}
    />
  );
}

function EmptyCard({
  title,
  body,
  ctaLabel,
  ctaHref,
  onRetry,
  retryLabel,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaLabel?: string;
  readonly ctaHref?: string;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        textAlign: 'center',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-3) auto 0 auto',
          maxWidth: 420,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          style={{
            ...primaryButton,
            marginTop: 'var(--space-4)',
            minHeight: 'var(--touch-target, 44px)',
          }}
        >
          {retryLabel}
        </button>
      ) : null}
      {ctaLabel && ctaHref ? (
        <a href={ctaHref} style={{ ...primaryButton, marginTop: 'var(--space-4)' }}>
          {ctaLabel}
        </a>
      ) : null}
    </section>
  );
}

const primaryButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--space-3) var(--space-6)',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  border: 'none',
  cursor: 'pointer',
};

const secondaryButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--space-3) var(--space-6)',
  borderRadius: 'var(--radius-pill)',
  background: 'transparent',
  color: 'var(--color-on-surface)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  border: '1px solid var(--color-border-strong)',
  cursor: 'pointer',
};
